import { PropsWithChildren, useEffect } from "react";
import {
  MeetingProvider,
  VoiceFocusProvider,
  BackgroundBlurProvider,
  BackgroundReplacementProvider,
  useVoiceFocus,
} from "amazon-chime-sdk-component-library-react";
import {
  AudioInputDevice,
  Device,
  VoiceFocusTransformDevice,
} from "amazon-chime-sdk-js";
import { useVideoBackgrounds } from "@platform/store";
import { REPLACEMENT_OPTIONS_LIST } from "@core/features/videoBackgrounds";
import { createBlob } from "../utils/background-replacement";
import { meetingConfig } from "../meetingConfig";
import { VideoFiltersCpuUtilization } from "@core/features/video";

function MeetingProviderWithDeviceReplacement({ children }: PropsWithChildren) {
  const { addVoiceFocus } = useVoiceFocus();

  const onDeviceReplacement = async (
    nextDevice: string,
    currentDevice: AudioInputDevice
  ): Promise<Device | VoiceFocusTransformDevice> => {
    return currentDevice instanceof VoiceFocusTransformDevice
      ? addVoiceFocus(nextDevice)
      : nextDevice;
  };

  return (
    <MeetingProvider onDeviceReplacement={onDeviceReplacement}>
      {children}
    </MeetingProvider>
  );
}

export function ChimeFeatureWrappers({ children }: PropsWithChildren) {
  const { cpuUtilization, imageBlob, option, setBlob } = useVideoBackgrounds();
  const logger = meetingConfig.logger;

  const filterEnabled =
    cpuUtilization !== VideoFiltersCpuUtilization.Disabled;
  const filterCPU = parseInt(cpuUtilization, 10) || 40;

  // Load background replacement image blob when option changes
  useEffect(() => {
    async function loadImage() {
      const match = REPLACEMENT_OPTIONS_LIST.find(
        (o) => option === o.label
      );
      if (match) {
        const blob = await createBlob(match);
        setBlob(blob);
      } else {
        logger.error(
          `Error: Cannot find ${option} in the replacementOptionsList: ${REPLACEMENT_OPTIONS_LIST}`
        );
      }
    }
    loadImage();
  }, [option, setBlob]);

  let wrapped = children;

  if (filterEnabled) {
    wrapped = (
      <BackgroundBlurProvider
        options={{ filterCPUUtilization: filterCPU, logger }}
      >
        <BackgroundReplacementProvider
          options={{ imageBlob, filterCPUUtilization: filterCPU, logger }}
        >
          {wrapped}
        </BackgroundReplacementProvider>
      </BackgroundBlurProvider>
    );
  }

  wrapped = (
    <VoiceFocusProvider spec={{ name: "default" }}>
      <MeetingProviderWithDeviceReplacement>
        {wrapped}
      </MeetingProviderWithDeviceReplacement>
    </VoiceFocusProvider>
  );

  return <>{wrapped}</>;
}
